import { ICommand, ProcessManagerContainer } from '@withlocals/cqrs.js/lib';
import { getCsrfToken } from 'modules/authentication/helpers/nextauth';
import { Observable } from 'rxjs';
import { isCordova } from 'utils/platform';

import { CordovaFbLogin, WebFbLogin } from './facebook';

export abstract class AuthCommand implements ICommand {}

export interface OAuth2Info {
  accessToken: string;
  tokenType?: string;
  expiresIn?: number;
  refreshToken?: string;
  userID: string;
  email?: string;
  companyName?: string;
}

export class LoginWithOAuth2 extends AuthCommand {
  constructor(
    readonly provider: string,
    readonly oauthInfo: OAuth2Info,
  ) {
    super();
  }
}

export class LoginWithEmail extends AuthCommand {
  password: () => string;

  constructor(
    readonly email: string,
    password: string,
    readonly companyName?: string,
    readonly csrfToken?: string,
  ) {
    super();
    this.password = () => {
      return password;
    };
  }
}

export class LoginWithPasswordToken extends AuthCommand {
  constructor(readonly token: string) {
    super();
  }
}

export class LoginWithAuthInfo extends AuthCommand {
  constructor(readonly authInfo) {
    super();
  }
}

export class LoginWithFacebook extends AuthCommand {
  constructor(readonly companyName?: string) {
    super();
  }
}

export class LoginWithGoogle extends AuthCommand {
  constructor(readonly companyName?: string) {
    super();
  }
}

export class LoginWithApple extends AuthCommand {
  constructor(readonly companyName?: string) {
    super();
  }
}

export class Signup extends AuthCommand {
  password: () => string;

  constructor(
    readonly firstName: string,
    readonly lastName: string,
    readonly email: string,
    password: string,
    readonly gender?: string,
    readonly phoneNumber?: string,
    readonly country?: string,
    readonly companyName?: string,
  ) {
    super();
    this.password = () => {
      return password;
    };
  }
}

export class ResetPassword extends AuthCommand {
  constructor(readonly email: string) {
    super();
  }
}

export class Logout extends AuthCommand {}

export class BecomeYourself extends AuthCommand {}

export class BecomePuppet extends AuthCommand {
  constructor(readonly id: string) {
    super();
  }
}

export class AuthActions extends ProcessManagerContainer {
  loginViaEmail = (
    username: string,
    password: string,
    companyName?: string,
  ) => {
    return Observable.fromPromise(
      getCsrfToken().then((csrfToken) => {
        return this.queue(
          new LoginWithEmail(username, password, companyName, csrfToken),
        );
      }),
    );
  };
  signup = (
    firstName: string,
    lastName: string,
    email: string,
    password: string,
    gender?: string,
    phoneNumber?: string,
    country?: string,
    companyName?: string,
  ) => {
    return this.queue(
      new Signup(
        firstName,
        lastName,
        email,
        password,
        gender,
        phoneNumber,
        country,
        companyName,
      ),
    );
  };
  logout = () => {
    return this.queue(new Logout());
  };
  becomeYourselfAgain = () => {
    return this.queue(new BecomeYourself());
  };
  becomePuppet = (id: string) => {
    return this.queue(new BecomePuppet(id));
  };
  facebookLogin = (companyName?: string) => {
    return this.queue(new LoginWithFacebook(companyName));
  };
  googleLogin = (companyName?: string) => {
    return this.queue(new LoginWithGoogle(companyName));
  };
  appleLogin = (companyName?: string) => {
    return this.queue(new LoginWithApple(companyName));
  };
  resetPassword = (email: string) => {
    return this.queue(new ResetPassword(email));
  };

  loginWithToken = (token: string) => {
    return this.queue(new LoginWithPasswordToken(token));
  };

  loginWithAuthInfo = (authInfo) => {
    return this.queue(new LoginWithAuthInfo(authInfo));
  };

  checkFbLoginStatus = () => {
    const fb = isCordova() ? new CordovaFbLogin() : new WebFbLogin();
    return fb.getLoginStatus();
  };
}
